import React, { useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useCart } from '../context/CartContext';
import { useTranslation } from '../hooks/useTranslation';
import { ordiniAPI, nexiAPI } from '../config/api';
import LanguageSelector from '../components/LanguageSelector';
import { useShippingSettings } from '../hooks/useShippingSettings';
import { detectShippingZone, getZoneSettings, ZONE_LABELS } from '../utils/shippingZones';
import './Checkout.css';

const Checkout = () => {
  const navigate = useNavigate();
  const { cart, getSubtotal } = useCart();
  const { t } = useTranslation();
  const { settings: shippingSettings, loading: shippingLoading, error: shippingError } = useShippingSettings();
  const subtotal = getSubtotal();

  const [formData, setFormData] = useState({
    nome: '',
    cognome: '',
    mail: '',
    telefono: '',
    via: '',
    civico: '',
    cap: '',
    citta: '',
    provincia: '',
    stato: '',
  });

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [isFormValid, setIsFormValid] = useState(false);
  const [createdOrderId, setCreatedOrderId] = useState(null);
  const [termsAccepted, setTermsAccepted] = useState(false);

  const privacyPolicyUrl = 'https://www.museostibbert.it/frontend/index.php?sez=page&isPrivacy=1';

  const detectedZone = useMemo(
    () => detectShippingZone(formData.stato),
    [formData.stato]
  );
  const zoneConfig = getZoneSettings(shippingSettings, detectedZone);
  const freeShippingMin = parseFloat(zoneConfig.free_shipping_min) || 0;
  const shippingCostValue = parseFloat(zoneConfig.shipping_cost) || 0;
  const fixedShippingEnabled = shippingSettings?.fixed_shipping_enabled ?? false;
  
  // Se fixedShippingEnabled è true, applica sempre il costo fisso
  // Altrimenti, se freeShippingMin è 0, la spedizione è sempre gratuita
  // Altrimenti, è gratuita solo se il subtotale è >= al minimo
  const qualifiesForFree = fixedShippingEnabled 
    ? false 
    : (freeShippingMin === 0 || subtotal >= freeShippingMin);
  const appliedShippingCost = shippingError ? 0 : (qualifiesForFree ? 0 : shippingCostValue);
  const orderTotal = subtotal + appliedShippingCost;
  const missingAmount = Math.max(0, freeShippingMin - subtotal);
  const shippingZoneLabel = zoneConfig.label || ZONE_LABELS[detectedZone];

  // Redirect se carrello vuoto
  if (cart.length === 0) {
    navigate('/carrello');
    return null;
  }

  const handleChange = (e) => {
    const newFormData = {
      ...formData,
      [e.target.name]: e.target.value,
    };
    setFormData(newFormData);
    if (error) {
      setError(null);
    }
    
    // Valida il form
    validateForm(newFormData);
  };

  // Funzione per validare il form
  const validateForm = (data) => {
    const required = ['nome', 'cognome', 'mail', 'via', 'civico', 'cap', 'citta', 'provincia', 'stato'];
    const isValid = required.every(field => data[field] && data[field].trim() !== '');
    setIsFormValid(isValid);
  };

  // Crea l'ordine nel backend se non esiste già (pagato=0)
  const ensureOrderCreated = async () => {
    if (createdOrderId) return createdOrderId;

    const fullName = `${formData.nome} ${formData.cognome}`.trim();
    const ordine = {
      cliente: {
        nome: fullName,
        mail: formData.mail,
        telefono: formData.telefono,
        via: formData.via,
        civico: formData.civico,
        cap: formData.cap,
        citta: formData.citta,
        provincia: formData.provincia,
        stato: formData.stato,
      },
      prodotti: cart.map(item => ({ id_prodotto: item.id, quantita: item.quantita })),
      pagato: 0,
    };

    const res = await ordiniAPI.create(ordine);
    setCreatedOrderId(res.id_ordine);
    return res.id_ordine;
  };

  // Avvia il pagamento Nexi (redirect Hosted Payment Page)
  const handleNexiPayment = async () => {
    try {
      setLoading(true);
      setError(null);
      if (!termsAccepted) {
        setLoading(false);
        setError(t('checkout.terms_error'));
        return;
      }
      // Assicurati che l'ordine esista
      const orderId = createdOrderId || await ensureOrderCreated();
      if (shippingLoading) {
        throw new Error(t('checkout.shipping_loading'));
      }

      if (shippingError) {
        throw new Error(t('checkout.shipping_error'));
      }

      const response = await nexiAPI.createOrder({
        order_id: orderId,
        amount: orderTotal.toFixed(2),
        currency: 'EUR',
      });

      if (response?.hosted_page_url) {
        window.location.href = response.hosted_page_url;
      } else {
        throw new Error('URL pagamento Nexi non disponibile');
      }
    } catch (err) {
      setError('Errore durante la creazione del pagamento Nexi: ' + err.message);
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="checkout-page">
      <div className="container">
        <h1 className="checkout-title">{t('checkout.title')}</h1>

        <div className="checkout-layout">
          {/* Form Dati Cliente */}
          <div className="checkout-form-section">
            <h2>{t('checkout.shipping_details')}</h2>
            
            <div className="checkout-form">
              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="nome">{t('checkout.form.name')} *</label>
                  <input
                    type="text"
                    id="nome"
                    name="nome"
                    value={formData.nome}
                    onChange={handleChange}
                    required
                    placeholder="Mario"
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="cognome">{t('checkout.form.surname')} *</label>
                  <input
                    type="text"
                    id="cognome"
                    name="cognome"
                    value={formData.cognome}
                    onChange={handleChange}
                    required
                    placeholder="Rossi"
                  />
                </div>
              </div>

              <div className="form-group">
                <label htmlFor="mail">{t('checkout.form.email')} *</label>
                <input
                  type="email"
                  id="mail"
                  name="mail"
                  value={formData.mail}
                  onChange={handleChange}
                  required
                  placeholder="mario.rossi@email.com"
                />
              </div>

              <div className="form-group">
                <label htmlFor="telefono">{t('checkout.form.phone')}</label>
                <input
                  type="tel"
                  id="telefono"
                  name="telefono"
                  value={formData.telefono}
                  onChange={handleChange}
                  placeholder="+39 123 456 7890"
                />
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="via">{t('checkout.form.street')} *</label>
                  <input
                    type="text"
                    id="via"
                    name="via"
                    value={formData.via}
                    onChange={handleChange}
                    required
                    placeholder="Via Example"
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="civico">{t('checkout.form.number')} *</label>
                  <input
                    type="text"
                    id="civico"
                    name="civico"
                    value={formData.civico}
                    onChange={handleChange}
                    required
                    placeholder="123"
                  />
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="cap">{t('checkout.form.postal_code')} *</label>
                  <input
                    type="text"
                    id="cap"
                    name="cap"
                    value={formData.cap}
                    onChange={handleChange}
                    required
                    placeholder="50100"
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="citta">{t('checkout.form.city')} *</label>
                  <input
                    type="text"
                    id="citta"
                    name="citta"
                    value={formData.citta}
                    onChange={handleChange}
                    required
                    placeholder="Firenze"
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="provincia">{t('checkout.form.province')} *</label>
                  <input
                    type="text"
                    id="provincia"
                    name="provincia"
                    value={formData.provincia}
                    onChange={handleChange}
                    required
                    placeholder="FI"
                  />
                </div>
              </div>

              <div className="form-group">
                <label htmlFor="stato">{t('checkout.form.state')} *</label>
                <input
                  type="text"
                  id="stato"
                  name="stato"
                  value={formData.stato}
                  onChange={handleChange}
                  required
                  placeholder="Italia"
                />
                {!shippingLoading && !shippingError && (
                  <p className="shipping-zone-inline">
                    {t('checkout.detected_zone', { values: { zone: shippingZoneLabel } })}
                  </p>
                )}
              </div>

              {error && (
                <div className="error-message">
                  ⚠️ {error}
                </div>
              )}

              <div className="form-actions">
                <button 
                  type="button" 
                  className="btn btn-outline"
                  onClick={() => navigate('/carrello')}
                  disabled={loading}
                >
                  ← {t('checkout.back_to_cart')}
                </button>
              </div>

              {/* Sezione Pagamento Nexi */}
              <div className="nexi-section">
                <h3>{t('checkout.complete_payment')}</h3>
                <p>{t('checkout.total')}: € {orderTotal.toFixed(2)}</p>
                <button
                  type="button"
                  className="btn btn-primary nexi-button"
                  onClick={handleNexiPayment}
                  disabled={!isFormValid || !termsAccepted || loading || shippingLoading || !!shippingError}
                >
                  {loading ? t('checkout.processing_payment') : t('checkout.pay_with_nexi')}
                </button>
                <p className="nexi-note">{t('checkout.redirect_to_nexi')}</p>
                {shippingLoading && (
                  <p className="shipping-note info">{t('checkout.shipping_loading')}</p>
                )}
                {shippingError && (
                  <p className="shipping-note warning">{t('checkout.shipping_error')}</p>
                )}
                <div className="terms-consent">
                  <label className="terms-checkbox">
                    <input
                      type="checkbox"
                      checked={termsAccepted}
                      onChange={(e) => setTermsAccepted(e.target.checked)}
                    />
                    <span>
                      {t('checkout.terms_intro')}{" "}
                      <Link to="/condizioni-di-acquisto" className="inline-link">
                        {t('checkout.terms_conditions')}
                      </Link>{" "}
                      {t('checkout.terms_connector')}{" "}
                      <a href={privacyPolicyUrl} target="_blank" rel="noopener noreferrer" className="inline-link">
                        {t('checkout.terms_privacy')}
                      </a>.
                    </span>
                  </label>
                  <p className="terms-warning-text">
                    {t('checkout.terms_warning')}
                  </p>
                </div>

                {!shippingLoading && !shippingError && !fixedShippingEnabled && (freeShippingMin > 0 || shippingCostValue > 0) && (
                  <p className={`shipping-note ${qualifiesForFree ? 'success' : 'info'}`}>
                    {qualifiesForFree
                      ? t('checkout.free_shipping_unlocked')
                      : t('checkout.free_shipping_missing', { values: { amount: missingAmount.toFixed(2) } })}
                  </p>
                )}
              </div>
            </div>
          </div>

          {/* Riepilogo Ordine */}
          <div className="checkout-summary">
            <h2>{t('checkout.order_summary')}</h2>
            
            <div className="summary-items">
              {cart.map(item => (
                <div key={item.id} className="summary-item">
                  <div className="summary-item-info">
                    <span className="summary-item-name">{item.nome}</span>
                    <span className="summary-item-qty">x{item.quantita}</span>
                  </div>
                  <span className="summary-item-price">
                    € {(parseFloat(item.prezzo) * item.quantita).toFixed(2)}
                  </span>
                </div>
              ))}
            </div>

            <div className="summary-divider"></div>

            <div className="summary-row">
              <span>{t('checkout.subtotal')}</span>
              <span>€ {subtotal.toFixed(2)}</span>
            </div>

            <div className="summary-row">
              <span>{t('checkout.shipping')}</span>
              <span className={qualifiesForFree ? 'shipping-free' : ''}>
                {shippingLoading
                  ? t('checkout.shipping_loading')
                  : shippingError
                    ? t('checkout.shipping_error')
                    : qualifiesForFree
                      ? t('checkout.shipping_free')
                      : `€ ${appliedShippingCost.toFixed(2)}`}
              </span>
            </div>

            {!shippingLoading && !shippingError && (
              <div className="shipping-zone-info">
                <span className="shipping-zone-pill">
                  {t('checkout.detected_zone', { values: { zone: shippingZoneLabel } })}
                </span>
                <p className="shipping-zone-hint">
                  {t('checkout.detected_zone_hint')}
                </p>
              </div>
            )}

            {!shippingLoading && !shippingError && !fixedShippingEnabled && (freeShippingMin > 0 || shippingCostValue > 0) && (
              <div className={`shipping-banner ${qualifiesForFree ? 'success' : 'info'}`}>
                {qualifiesForFree
                  ? t('checkout.free_shipping_unlocked')
                  : t('checkout.free_shipping_missing', { values: { amount: missingAmount.toFixed(2) } })}
              </div>
            )}

            {shippingError && (
              <div className="shipping-banner warning">
                {t('checkout.shipping_warning')}
              </div>
            )}

            <div className="summary-divider"></div>

            <div className="summary-total">
              <span>{t('checkout.total')}</span>
              <span>€ {orderTotal.toFixed(2)}</span>
            </div>

            <div className="payment-note">
              <p><strong>{t('checkout.payment_methods')}:</strong> {t('checkout.payment_methods_desc')}</p>
              <p><strong>{t('checkout.security')}:</strong> {t('checkout.security_desc')}</p>
            </div>
          </div>
        </div>
      </div>
      
      {/* Language Selector */}
      <LanguageSelector />
    </div>
  );
};

export default Checkout;

